import type { Route } from "../../../utils/models/Route";
import { cumulativeDistances, MeasuredLine, pointAtDistance } from "../utils/trailLine";
import { MIN_DAY_M, nearestAlongLine } from "./day-flags";
import type { NearbyPlace } from "./route-endpoints";
import type { PlaceKinds } from "./nearby-places";
import { PlacePick, pickCoordinates } from "./place-picks";

/**
 * The AI Planner's nights (AiPlannerModal.vue): where each night falls when
 * the walking time is shared out evenly at the walker's pace, then the place
 * to stay nearest each of those — when there is one close enough. Pure.
 */

export type StayKind = 'beds' | 'camping' | 'any';

export const STAY_KINDS: { [kind in StayKind]: PlaceKinds } = {
  beds: { accommodationTypes: ['bnb', 'hostel', 'hotel', 'motel'], amenityTypes: [] },
  camping: { accommodationTypes: ['campground'], amenityTypes: ['osmcampsite'] },
  any: { accommodationTypes: ['bnb', 'campground', 'hostel', 'hotel', 'motel'], amenityTypes: ['osmcampsite'] },
};

/** How far off the route a place to stay may be. */
export const STAY_SEARCH_BUFFER_M = 2000;

/**
 * How far a night may move from where the pace puts it, as a share of an
 * average day — close first, then further before giving up on that night.
 */
export const NIGHT_WINDOWS = [0.2, 0.4];

/** No day comes out shorter than this share of an average day. */
const MIN_DAY_SHARE = 0.3;

/**
 * Each metre off the route counts this much against a place: it's walked
 * there in the evening and back in the morning, usually on a road.
 */
const OFF_ROUTE_WEIGHT = 2;

/**
 * A place counts as belonging to another stretch when the route passes this
 * many times nearer it there (plus SAME_SPOT_M) — not merely a little nearer,
 * as it often does just past a window's edge.
 */
const NEARER_ELSEWHERE = 1.5;
const SAME_SPOT_M = 50;

export interface Pace {
  secondsDuration(elevationGainMeters: number, distanceMeters: number): number;
}

/**
 * Walking time from the start to each point of the line, at `pace` — climbs
 * counted from the points' elevations where they have them.
 */
export function walkingSeconds(line: MeasuredLine, pace: Pace): number[] {
  const { coordinates, cumDist } = line;
  const seconds = [0];
  for (let i = 1; i < coordinates.length; i++) {
    const from = coordinates[i - 1][2];
    const to = coordinates[i][2];
    const climb = Number.isFinite(from) && Number.isFinite(to) ? Math.max(0, to - from) : 0;
    seconds.push(seconds[i - 1] + pace.secondsDuration(climb, cumDist[i] - cumDist[i - 1]));
  }
  return seconds;
}

/**
 * Where each night falls, in metres along the line, sharing the walking time
 * evenly between `dayCount` days.
 */
export function nightTargets(cumDist: number[], cumSeconds: number[], dayCount: number): number[] {
  const total = cumSeconds[cumSeconds.length - 1] ?? 0;
  const targets: number[] = [];
  if (dayCount < 2 || cumDist.length < 2) return targets;
  if (total <= 0) {
    const length = cumDist[cumDist.length - 1];
    for (let night = 1; night < dayCount; night++) targets.push((length * night) / dayCount);
    return targets;
  }
  let i = 1;
  for (let night = 1; night < dayCount; night++) {
    const wanted = (total * night) / dayCount;
    while (i < cumSeconds.length - 1 && cumSeconds[i] < wanted) i++;
    const span = cumSeconds[i] - cumSeconds[i - 1];
    const t = span > 0 ? (wanted - cumSeconds[i - 1]) / span : 0;
    targets.push(cumDist[i - 1] + (cumDist[i] - cumDist[i - 1]) * Math.min(1, Math.max(0, t)));
  }
  return targets;
}

export interface NightStop {
  /** 1 for the first night. */
  night: number;
  /** Where the pace puts the night, in metres along the line. */
  targetM: number;
  /** Where the night actually leaves the line, in metres along it. */
  atM: number;
  /** The place to stay — null when none was close enough, so the night is on the route at `targetM`. */
  place: NearbyPlace | null;
  /** How far the place is off the route, in metres (0 without one). */
  offRouteM: number;
  /** [lon, lat] where the night leaves the route — to find it again if the route's measured afresh. */
  point?: [number, number];
  /** Where they chose to stay instead, searching for it themselves (see chooseNightStay). */
  pick?: PlacePick;
}

/**
 * For each night in turn, the place to stay nearest where the pace puts it —
 * close by if there is one, a little further if not — keeping every day a
 * reasonable length. Nights with nothing near enough stay on the route.
 */
export function planNights(line: MeasuredLine, targets: number[], places: NearbyPlace[]): NightStop[] {
  const totalM = line.cumDist[line.cumDist.length - 1] ?? 0;
  const averageDayM = totalM / (targets.length + 1);
  const minDayM = averageDayM * MIN_DAY_SHARE;
  const stops: NightStop[] = [];

  targets.forEach((targetM, i) => {
    const previousM = stops[i - 1]?.atM ?? 0;
    let stop: NightStop = { night: i + 1, targetM, atM: targetM, place: null, offRouteM: 0 };
    for (const window of NIGHT_WINDOWS) {
      const fromM = Math.max(targetM - window * averageDayM, previousM + minDayM);
      const toM = Math.min(targetM + window * averageDayM, totalM - minDayM);
      if (toM <= fromM) continue;
      let best: { place: NearbyPlace; atM: number; offRouteM: number; score: number } | null = null;
      for (const place of places) {
        const nearest = nearestAlongLine(line, place.coordinates, { fromM, toM });
        if (nearest.offLineM > STAY_SEARCH_BUFFER_M) continue;
        // Much nearer the route somewhere outside the window (on it, say, a
        // little further on): it's a stop for another stretch, not one off
        // the route from this one.
        if (nearest.offLineM > nearestAlongLine(line, place.coordinates).offLineM * NEARER_ELSEWHERE + SAME_SPOT_M) continue;
        const score = Math.abs(nearest.distanceM - targetM) + OFF_ROUTE_WEIGHT * nearest.offLineM;
        if (!best || score < best.score) best = { place, atM: nearest.distanceM, offRouteM: nearest.offLineM, score };
      }
      if (best) {
        stop = { night: i + 1, targetM, atM: best.atM, place: best.place, offRouteM: best.offRouteM };
        break;
      }
    }
    // A night with no place stays where the pace puts it, keeping the days
    // either side of it long enough.
    if (!stop.place) stop.atM = Math.min(Math.max(targetM, previousM + minDayM), totalM - minDayM);
    stop.point = pointOn(line, stop.atM);
    stops.push(stop);
  });
  return stops;
}

/**
 * The plan with night `index` moved to where the walker chose to stay: it
 * leaves the route at the nearest point to their choice that still falls
 * between the nights either side.
 */
export function chooseNightStay(line: MeasuredLine, stops: NightStop[], index: number, pick: PlacePick): NightStop[] {
  const totalM = line.cumDist[line.cumDist.length - 1] ?? 0;
  const before = stops[index - 1]?.atM ?? 0;
  const after = stops[index + 1]?.atM ?? totalM;
  // Neighbours too close to leave room either side: halfway between them.
  const range = after - before > MIN_DAY_M * 2
    ? { fromM: before + MIN_DAY_M, toM: after - MIN_DAY_M }
    : { fromM: (before + after) / 2, toM: (before + after) / 2 };
  const nearest = nearestAlongLine(line, pickCoordinates(pick), range);
  return stops.map((stop, i) => (i !== index ? stop : {
    ...stop,
    atM: nearest.distanceM,
    place: pick.kind === 'place' ? pick.place : null,
    offRouteM: nearest.offLineM,
    point: [nearest.point[0], nearest.point[1]] as [number, number],
    pick,
  }));
}

function pointOn(line: MeasuredLine, distanceM: number): [number, number] {
  const point = pointAtDistance(line.coordinates, line.cumDist, distanceM);
  return [point[0], point[1]];
}

/**
 * A plan's main routes walked one after another, as one measured line —
 * gaps between them crossed straight — for planning the days of a walk not
 * yet joined up (applyNightPlan joins it). Null without one.
 */
export function walkLine(routes: { [routeKey: string]: Route }): MeasuredLine | null {
  const coordinates = Object.values(routes)
    .filter(route => (route.route_style ?? 'route') === 'route')
    .sort((a, b) => (a.day ?? 1) - (b.day ?? 1) || (a.position ?? 0) - (b.position ?? 0))
    .flatMap(route => (route.lineStrings ?? []).flatMap((lineString: any) => lineString?.lineStringGeometry?.coordinates ?? []));
  if (coordinates.length < 2) return null;
  return { coordinates, cumDist: cumulativeDistances(coordinates) };
}

/**
 * Where each of a plan's nights falls on `line` — found again from the point
 * it leaves the route, for when the route has been joined up or re-measured
 * since the plan was made — in order, each a day on from the last.
 */
export function nightsAlong(line: MeasuredLine, stops: NightStop[]): number[] {
  const totalM = line.cumDist[line.cumDist.length - 1] ?? 0;
  const nights: number[] = [];
  stops.forEach((stop, i) => {
    const fromM = (nights[i - 1] ?? 0) + MIN_DAY_M;
    const toM = totalM - MIN_DAY_M * (stops.length - i);
    if (!stop.point || toM <= fromM) {
      // Never past the end, however little room a short walk leaves.
      nights.push(Math.min(Math.max(stop.atM, fromM), Math.max(fromM, toM), totalM));
      return;
    }
    nights.push(nearestAlongLine(line, stop.point, { fromM, toM }).distanceM);
  });
  return nights;
}
